import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const TaskContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * TaskProvider - Single source of truth for tasks, accounts and team members.
 *
 * All admin pages read their data from here instead of fetching independently,
 * so a mutation on one page is reflected everywhere. Any page that changes a
 * task should call refreshTasks() afterwards to re-sync the whole app.
 *
 * Data consistency rules enforced here (at the source):
 *  - Tasks belonging to archived/deleted accounts (accounts no longer returned
 *    by GET /api/accounts) are filtered out entirely.
 *  - activeTasks additionally excludes tasks that have been archived globally
 *    (archivedGlobally: true).
 */
export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]); // Tasks from active accounts only
  const [accounts, setAccounts] = useState([]); // Active (non-archived) accounts
  const [teamMembers, setTeamMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshTasks = useCallback(async () => {
    try {
      const [tasksRes, accountsRes, membersRes, statsRes] = await Promise.all([
        axios.get(`${API}/tasks?include_archived=true`),
        axios.get(`${API}/accounts`), // Only active (non-archived) accounts
        axios.get(`${API}/team-members`),
        axios.get(`${API}/dashboard/stats`)
      ]);

      const activeAccounts = accountsRes.data || [];
      const activeAccountIds = new Set(activeAccounts.map((a) => a.id));

      // Filter out tasks from archived/deleted accounts at the source.
      // Tasks with no accountId (unassigned) are always kept.
      const visibleTasks = (tasksRes.data || []).filter(
        (t) => !t.accountId || activeAccountIds.has(t.accountId)
      );

      setTasks(visibleTasks);
      setAccounts(activeAccounts);
      setTeamMembers(membersRes.data || []);
      setStats(statsRes.data || null);

      // Notify any component keeping its own local copy of the data so it can
      // re-sync after a mutation happens elsewhere.
      window.dispatchEvent(new Event('taskMutated'));
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshTasks();
  }, [refreshTasks]);

  // activeTasks excludes globally archived tasks (used for stats/active views).
  const activeTasks = tasks.filter((t) => !t.archivedGlobally);

  const value = {
    tasks, // All tasks from active accounts (includes archived tasks)
    activeTasks, // Excludes archivedGlobally tasks
    accounts,
    teamMembers,
    stats,
    loading,
    refreshTasks
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

export const useTasks = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTasks must be used within TaskProvider');
  }
  return context;
};
